import React, { useEffect, useRef, useState } from "react";
import { useDeal } from "../lib/store";
import { Card } from "../components/shared";
import { CIMDoc } from "../components/CIMDoc";
import { Pitchbook, PB_SLIDES } from "../components/Pitchbook";
import { PitchbookConfig } from "../components/PitchbookConfig";
import { savePdfFromContainer } from "../lib/pdf";
import { pptxFromDeck } from "../lib/deckPptx";
import { dealKeyFor, dealKeyAlts } from "../components/ShareRoomModal";
import { exportApi, shareApi, firmApi } from "../lib/api";
import * as m from "../lib/model";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { FileText, GripVertical, Presentation, Printer, Sheet, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function ExportMemo() {
  const { M, market, navigate, dealId } = useDeal();
  const hasRollup = m.hasRollup(M);
  const docRef = useRef(null);
  const exportRef = useRef(null);
  const pbRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pptBusy, setPptBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [mode, setMode] = useState("doc");
  const [share, setShare] = useState(null);
  const [firm, setFirm] = useState(null);
  const [pbSecs, setPbSecs] = useState(Object.fromEntries(PB_SLIDES.map((s) => [s.id, true])));
  const [pbOv, setPbOv] = useState({});
  const [deckCover, setDeckCover] = useState("");

  useEffect(() => {
    shareApi.byDeal(dealKeyFor(dealId, M), dealKeyAlts(dealId, M)).then(setShare).catch(() => setShare(null));
    firmApi.get().then(setFirm).catch(() => {});
  }, [dealId, M.dealKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reuse the room's platform banner photos unless a deck-only cover is uploaded.
  const roomPhotos = (() => {
    const b = (share && share.thesis && share.thesis.thePlatform) || {};
    return [...(Array.isArray(b.photos) ? b.photos : []), b.photo].filter(Boolean);
  })();
  const coverList = deckCover ? deckCover.split("|").filter(Boolean) : roomPhotos;

  const buildDeck = async () => {
    if (!pbRef.current) return;
    setBusy(true);
    const tid = toast.loading("Building pitchbook…");
    try {
      const pages = await savePdfFromContainer(pbRef.current, `XYZ-Ventures-Pitchbook-${(displayName || "deal").replace(/\s+/g, "-")}.pdf`, "[data-pb-slide]", { orientation: "landscape", scale: 1.7, pad: 0 });
      toast.success(`Pitchbook downloaded — ${pages} slides`, { id: tid });
    } catch (e) {
      toast.error("Could not build the pitchbook", { id: tid });
    } finally { setBusy(false); }
  };

  const buildPptx = async () => {
    if (!pbRef.current) return;
    setPptBusy(true);
    const tid = toast.loading("Building editable PowerPoint…");
    try {
      const slides = await pptxFromDeck(pbRef.current, `XYZ-Ventures-Pitchbook-${(displayName || "deal").replace(/\s+/g, "-")}.pptx`, coverList);
      toast.success(`PowerPoint downloaded — ${slides} editable slides`, { id: tid });
    } catch (e) {
      console.error("pptx export failed", e);
      toast.error(`Could not build the PowerPoint — ${e?.message || "unknown error"}`, { id: tid });
    } finally { setPptBusy(false); }
  };

  const printDeck = () => {
    document.body.classList.add("printing-pb");
    const done = () => { document.body.classList.remove("printing-pb"); window.removeEventListener("afterprint", done); };
    window.addEventListener("afterprint", done);
    setTimeout(() => window.print(), 80);
  };

  const exportAiModel = async () => {
    setAiBusy(true);
    const tid = toast.loading("Populating firm underwriting template…");
    try {
      const res = await exportApi.templateModel({ deal: M, name: displayName || M.dealName });
      const bytes = Uint8Array.from(atob(res.content), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url; a.download = res.filename; a.click();
      URL.revokeObjectURL(url);
      toast.success("Template populated — all formulas intact", { id: tid });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Template export failed — try again", { id: tid });
    } finally { setAiBusy(false); }
  };

  const SECTIONS = [
    { id: "cover", label: "Cover page", desc: "Title, industry, preparer, NDA notice", always: false },
    { id: "summary", label: "Executive summary", desc: "Thesis + headline metrics" },
    { id: "business", label: "Business overview", desc: "Industry, HQ, employees, deal type" },
    { id: "financials", label: "Financial snapshot", desc: "3-yr history + forecast" },
    { id: "structure", label: "Sources & uses", desc: "How the deal is funded" },
    { id: "financing", label: "Financing / senior debt", desc: "Debt terms & DSCR" },
    { id: "returns", label: "Returns & exit", desc: "LP MOIC / IRR, exit value" },
    { id: "rollup", label: "Roll-up opportunity", desc: hasRollup ? "Targets + consolidated EBITDA/returns" : "No roll-up targets added yet", disabled: !hasRollup },
    { id: "strengths", label: "Strengths", desc: "Deal strengths from the model" },
    { id: "weaknesses", label: "Weaknesses & key risks", desc: "Risks the LP should underwrite" },
    { id: "market", label: "Market intelligence & comps", desc: market ? "Sector comps, consolidators, tailwinds" : "Generate on AI Evaluation first", disabled: !market },
  ];

  const [sec, setSec] = useState({ cover: true, summary: true, business: true, financials: true, structure: true, financing: true, returns: true, rollup: hasRollup, strengths: true, weaknesses: true, market: !!market });
  const toggle = (id) => setSec((s) => ({ ...s, [id]: !s[id] }));
  const selectedCount = SECTIONS.filter((s) => sec[s.id] && !s.disabled).length;
  const [displayName, setDisplayName] = useState(M.dealName || "");
  const [industry, setIndustry] = useState(M.industry || "");
  const [location, setLocation] = useState(M.address || "");
  const [overview, setOverview] = useState("");

  const buildPdf = async () => {
    if (selectedCount === 0) return toast.error("Select at least one section");
    setBusy(true);
    const tid = toast.loading("Building CIM…");
    try {
      const node = exportRef.current;
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true, windowWidth: node.scrollWidth });
      const img = canvas.toDataURL("image/jpeg", 0.95);
      const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
      const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight();
      // Fit the ENTIRE document onto a single page (scale to fit height, keep all data).
      let w = pw, h = (canvas.height * pw) / canvas.width;
      if (h > ph) { const s = ph / h; h = ph; w = pw * s; }
      pdf.addImage(img, "JPEG", (pw - w) / 2, 0, w, h);
      pdf.save(`XYZ-Ventures-CIM-${(displayName || "deal").replace(/\s+/g, "-")}.pdf`);
      toast.success("CIM PDF downloaded", { id: tid });
    } catch (e) {
      toast.error("Could not build the CIM", { id: tid });
    } finally { setBusy(false); }
  };

  const exportExcel = () => {
    try {
      const wb = XLSX.utils.book_new();
      const ex = m.exitReturns(M), wf = m.lpWaterfall(M);
      const tc = m.transCostsTotal ? m.transCostsTotal(M) : 0;
      const senior = m.loanAmt(M), sn = M.snAmt || 0, eq = ex.equityInvested || 0;
      const totalSrc = senior + sn + eq;
      const totalUses = (M.askPrice || 0) + tc;
      const pct = (n) => (totalSrc ? Number((n / totalSrc).toFixed(4)) : 0);

      // Summary (PPM style)
      const summary = [
        ["Summary"], [],
        ["Project:", "", "Deal Overview:"],
        ["Company Name", M.companyName || M.dealName, "", "Exit Year", M.holdIndefinite ? "Hold" : M.hold],
        ["Project Name", displayName || M.dealName, "", "Preferred Return", (M.prefReturn || 0) / 100],
        ["Industry", M.industry || industry, "", "Exit Multiple", M.exitMult],
        ["Location", M.address || location, "", "Equity", eq],
        [], ["Company:", "", "", "% of Equity", totalSrc ? Number((eq / totalSrc).toFixed(4)) : 0],
        ["Revenue (TTM)", M.ttmRevenue, "", "Debt", senior + sn],
        ["Adj. EBITDA / SDE (TTM)", M.sde, "", "% of Debt", totalSrc ? Number(((senior + sn) / totalSrc).toFixed(4)) : 0],
        ["Entry multiple", M.sde ? Number((m.nac(M) / M.sde).toFixed(2)) : 0],
        [], ["Modeled LP MOIC", Number((wf.lpMoic || 0).toFixed(2))], ["Modeled LP IRR %", isNaN(wf.lpIrr) ? "n/a" : Number((wf.lpIrr * 100).toFixed(1))],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Summary");

      // Sources & Uses (PPM two-column, % of Total + $ Value)
      const su = [
        ["PPM: Sources & Uses"], [],
        ["Sources of Capital", "% of Total", "$ Value", "", "Uses of Capital", "% of Total", "$ Value"],
        ["Equity", "", "", "", "Business / Goodwill", totalUses ? Number(((M.askPrice || 0) / totalUses).toFixed(4)) : 0, M.askPrice],
        ["Sponsor / Investor Equity", pct(eq), eq, "", "Transaction Costs", totalUses ? Number((tc / totalUses).toFixed(4)) : 0, tc],
        ["Total Equity", pct(eq), eq],
        [], ["Debt"],
        ["Senior Debt", pct(senior), senior],
        ["Seller Note", pct(sn), sn],
        ["Total Debt", pct(senior + sn), senior + sn],
        [], ["Total Sources", 1, totalSrc, "", "Total Uses", 1, totalUses],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(su), "Sources & Uses");

      // Historical & Pro Forma (revenue, GP, margin, EBITDA across years)
      const hist = (M.historicalYears || []).slice(-3);
      const g = 1 + (M.sdeGrowth || 0) / 100;
      const lastR = (hist[hist.length - 1] || {}).revenue || M.ttmRevenue || 0;
      const lastS = M.sde || 0;
      const nYrs = M.holdIndefinite ? 5 : Math.max(1, Math.min(M.hold || 5, 7));
      const projHead = [], projRev = [], projSde = [], projGm = [];
      for (let i = 1; i <= nYrs; i++) { projHead.push(`FY+${i} (P)`); projRev.push(Math.round(lastR * Math.pow(g, i))); projSde.push(Math.round(lastS * Math.pow(g, i))); projGm.push(M.grossMarginPct); }
      const hf = [
        ["PPM: Historical & Pro Forma"], [],
        ["", ...hist.map((h) => h.year), ...projHead],
        ["Total Revenue", ...hist.map((h) => h.revenue), ...projRev],
        ["Gross Margin %", ...hist.map((h) => m.pctOf(h.gm)), ...projGm],
        ["Net Income", ...hist.map((h) => h.netIncome), ...projRev.map(() => "")],
        ["Adj. EBITDA / SDE", ...hist.map((h) => h.sde), ...projSde],
        [], ["Annual debt service", m.totalAnnualDS(M)], ["CFADS", m.cfads(M)], ["Year-1 DSCR", Number(m.dscrYr1(M).toFixed(2))],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(hf), "Historical & Pro Forma");

      // Returns
      const rr = [["Returns"], [],
        ["Sponsor / investor equity", eq], ["Exit SDE / EBITDA", ex.sdeExit],
        ["Exit enterprise value", M.holdIndefinite ? "No exit — cash-flow hold" : ex.exitEV], ["Remaining loan balance", ex.loanBal],
        ["Equity at exit", ex.equityAtExit], ["Modeled LP MOIC", Number((wf.lpMoic || 0).toFixed(2))],
        ["Modeled LP IRR %", isNaN(wf.lpIrr) ? "n/a" : Number((wf.lpIrr * 100).toFixed(1))],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rr), "Returns");

      XLSX.writeFile(wb, `XYZ-Ventures-Model-${(displayName || "deal").replace(/\s+/g, "-")}.xlsx`);
      toast.success("Excel workbook downloaded");
    } catch (e) { toast.error("Could not build the workbook"); }
  };

  return (
    <div className="page" data-testid="page-export">
      <div className="page-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="page-title">CIM Builder</div>
          <div className="page-sub">{mode === "doc"
            ? `Assemble a client-ready Confidential Information Memorandum for ${M.dealName} — pick the sections, then export a branded PDF`
            : `A 16:9 investor pitchbook for ${M.dealName} — every figure reads from the live model${share ? " and your published deal room" : ""}`}</div>
        </div>
        <div className="scen-bar no-print" data-testid="cim-mode">
          <button className={mode === "doc" ? "active" : ""} onClick={() => setMode("doc")} data-testid="cim-mode-doc">Document</button>
          <button className={mode === "deck" ? "active" : ""} onClick={() => setMode("deck")} data-testid="cim-mode-deck">Pitchbook</button>
        </div>
      </div>

      {mode === "deck" ? (
        <>
          <div className="grid g2" style={{ alignItems: "start" }}>
            <PitchbookConfig secs={pbSecs} setSecs={setPbSecs} ov={pbOv} setOv={setPbOv}
              cover={deckCover} setCover={setDeckCover} fallback={roomPhotos} blActive={!!(m.backLever(M) || {}).active} />
            <Card title="Export" testid="pb-export">
              <div className="field"><label>Deck title</label><input className="inp" value={displayName} onChange={(e) => setDisplayName(e.target.value)} data-testid="pb-title" /></div>
              <div className="ra-sub" style={{ marginTop: 8 }}>
                Headlines and narrative come from your published deal room where available, so the deck, the room and the teaser all tell the same story. Type over any slide in the list on the left.
              </div>
              <button className="btn btn-primary" style={{ width: "100%", marginTop: 12 }} onClick={buildDeck} disabled={busy} data-testid="pb-generate">
                <FileText size={14} style={{ marginRight: 6 }} />{busy ? "Building pitchbook…" : "Download Pitchbook PDF (landscape)"}
              </button>
              <button className="btn btn-primary" style={{ width: "100%", marginTop: 8 }} onClick={buildPptx} disabled={pptBusy} data-testid="pb-pptx">
                <Presentation size={13} style={{ marginRight: 6 }} />{pptBusy ? "Building PowerPoint…" : "Download Editable PowerPoint (.pptx)"}
              </button>
              <button className="btn" style={{ width: "100%", marginTop: 8 }} onClick={printDeck} data-testid="pb-print"><Printer size={13} style={{ marginRight: 6 }} />Print / Save as PDF</button>
              <button className="btn btn-share" style={{ width: "100%", marginTop: 8 }} onClick={() => navigate("dealroom")} data-testid="pb-share-room"><Share2 size={13} style={{ marginRight: 6 }} />Edit the room narrative</button>
            </Card>
          </div>
          <Card title="Slide preview" className="pb-host" style={{ marginTop: 12 }} testid="pb-preview">
            <div className="pb-scroll">
              <div className="pb-zoom" style={{ transform: "scale(0.62)", width: 1120, height: `${630 * 0.62 * Math.max(1, PB_SLIDES.filter((x) => pbSecs[x.id]).length) + 200}px` }}>
                <Pitchbook M={M} market={market} share={share} firm={firm} cover={coverList} ov={pbOv} secs={pbSecs} title={displayName || M.dealName} />
              </div>
            </div>
          </Card>
          {/* Unscaled offscreen copy: html2canvas mis-measures text inside a CSS transform, so the PDF is shot from here. */}
          <div style={{ position: "fixed", left: -99999, top: 0, width: 1120, pointerEvents: "none" }} aria-hidden>
            <Pitchbook ref={pbRef} M={M} market={market} share={share} firm={firm} cover={coverList} ov={pbOv} secs={pbSecs} title={displayName || M.dealName} />
          </div>
        </>
      ) : (
      <>
      <div className="grid g2" style={{ alignItems: "start" }}>
        <Card title={`Sections to include (${selectedCount})`} testid="cim-sections">
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {SECTIONS.map((s) => (
              <label key={s.id} className="cim-sec-row" data-testid={`cim-sec-${s.id}`} style={{ opacity: s.disabled ? 0.45 : 1, cursor: s.disabled ? "not-allowed" : "pointer" }}>
                <input type="checkbox" checked={!!sec[s.id] && !s.disabled} disabled={s.disabled} onChange={() => !s.disabled && toggle(s.id)} data-testid={`cim-chk-${s.id}`} />
                <GripVertical size={13} style={{ color: "var(--text3)" }} />
                <span style={{ flex: 1 }}>
                  <span style={{ fontSize: 13, color: "var(--text)" }}>{s.label}</span>
                  <span style={{ display: "block", fontSize: 10.5, color: "var(--text3)" }}>{s.desc}</span>
                </span>
              </label>
            ))}
          </div>
        </Card>

        <Card title="Cover details" testid="cim-cover-form">
          <div className="field"><label>Opportunity name (blind)</label><input className="inp" value={displayName} onChange={(e) => setDisplayName(e.target.value)} data-testid="cim-name" /></div>
          <div className="field" style={{ marginTop: 8 }}><label>Industry</label><input className="inp" value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder={M.industry || "e.g. Commercial landscaping"} data-testid="cim-industry" /></div>
          <div className="field" style={{ marginTop: 8 }}><label>Location</label><input className="inp" value={location} onChange={(e) => setLocation(e.target.value)} placeholder={M.address || "e.g. DFW Metroplex"} data-testid="cim-location" /></div>
          <div className="field" style={{ marginTop: 8 }}><label>Executive summary (optional — leave blank to auto-write)</label><textarea className="inp" rows={4} value={overview} onChange={(e) => setOverview(e.target.value)} placeholder="Auto-generated from the deal model if left blank." data-testid="cim-overview" /></div>
          <button className="btn btn-primary" style={{ width: "100%", marginTop: 12 }} onClick={buildPdf} disabled={busy} data-testid="cim-generate"><FileText size={14} style={{ marginRight: 6 }} />{busy ? "Building CIM…" : `Generate CIM PDF (${selectedCount} sections)`}</button>
          <button className="btn btn-primary" style={{ width: "100%", marginTop: 8 }} onClick={exportAiModel} disabled={aiBusy} data-testid="cim-ai-model"><Sheet size={13} style={{ marginRight: 6 }} />{aiBusy ? "Populating firm template…" : "Populate Firm Excel Template"}</button>
          <button className="btn" style={{ width: "100%", marginTop: 8 }} onClick={exportExcel} data-testid="cim-excel"><Sheet size={13} style={{ marginRight: 6 }} />Quick Excel (instant, no AI)</button>
          <button className="btn" style={{ width: "100%", marginTop: 8 }} onClick={() => window.print()} data-testid="cim-print"><Printer size={13} style={{ marginRight: 6 }} />Print / Save as PDF</button>
          <button className="btn btn-share" style={{ width: "100%", marginTop: 8 }} onClick={() => navigate("dealroom")} data-testid="cim-share-room"><Share2 size={13} style={{ marginRight: 6 }} />Share Web CIM with investors</button>
        </Card>
      </div>

      <Card title="Live preview" style={{ marginTop: 12 }} testid="cim-preview">
        <div className="cim-preview-scroll">
          <CIMDoc ref={docRef} M={M} sec={sec} market={market} displayName={displayName} industry={industry} location={location} overview={overview} />
        </div>
      </Card>

      {/* Offscreen full-height render used for PDF export (avoids scroll-container clipping) */}
      <div style={{ position: "fixed", left: -99999, top: 0, width: 1040, background: "#fff", pointerEvents: "none" }} aria-hidden>
        <CIMDoc ref={exportRef} M={M} sec={sec} market={market} displayName={displayName} industry={industry} location={location} overview={overview} />
      </div>
      </>
      )}
    </div>
  );
}
